import React from "react";
import AppLayout from "../components/AppLayout";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { motion } from "framer-motion";
import { Loader2, ClipboardList, RefreshCw, CheckCircle2, Clock } from "lucide-react";
import { useCachedApi, timeAgo } from "../lib/useCachedApi";

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

const ActionPlan = () => {
    const { language, user } = useApp();
    const uid = user?.user_id || "anon";
    const { data, generatedAt, loading, refreshing, error, refetch } = useCachedApi(
        `vlg:cache:${uid}:${language}:ai-action-plan`,
        "/ai/action-plan",
        { ttlMs: 6 * 60 * 60 * 1000 } // 6h TTL
    );
    const plan = data?.plan || [];

    return (
        <AppLayout title={t(language, "nav_action_plan")}>
            <Card className="rounded-3xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-none mb-8" data-testid="action-plan-hero">
                <CardContent className="p-8 md:p-10 flex items-center justify-between flex-wrap gap-4">
                    <div className="max-w-xl">
                        <div className="text-xs tracking-[0.28em] font-bold uppercase opacity-70 flex items-center gap-2">
                            <ClipboardList className="w-3.5 h-3.5" /> {language === "es" ? "PLAN PERSONALIZADO DE CAESAR" : "CAESAR'S PERSONALIZED PLAN"}
                        </div>
                        <div className="font-display text-3xl md:text-4xl font-bold mt-3">{t(language, "action_plan_title")}</div>
                        <div className="mt-3 opacity-70 text-sm">
                            {language === "es" ? "Basado en tus transacciones reales de los últimos 90 días." : "Based on your last 90 days of real transactions."}
                        </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                        <button
                            data-testid="refresh-plan-button"
                            onClick={refetch}
                            disabled={refreshing || loading}
                            aria-label={language === "es" ? "Refrescar plan" : "Refresh plan"}
                            title={language === "es" ? "Refrescar plan" : "Refresh plan"}
                            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] opacity-80 hover:opacity-100 transition-opacity disabled:opacity-50 pill px-4 py-2 bg-white/10 hover:bg-white/15"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
                            {language === "es" ? "Refrescar" : "Refresh"}
                        </button>
                        {generatedAt && (
                            <div data-testid="freshness-indicator" className="inline-flex items-center gap-1.5 text-[10px] font-medium opacity-70">
                                <Clock className="w-3 h-3" />
                                {language === "es" ? "Generado" : "Generated"} {timeAgo(generatedAt, language)}
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {loading && !data ? (
                <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] py-6" data-testid="action-plan-loading">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {language === "es" ? "Caesar está armando tu plan…" : "Caesar is building your plan…"}
                </div>
            ) : error && plan.length === 0 ? (
                <div className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="action-plan-error">
                    {language === "es"
                        ? "No pudimos generar tu plan ahora mismo. Inténtalo de nuevo."
                        : "We couldn't generate your plan just now. Try again."}
                </div>
            ) : plan.length === 0 ? (
                <div className="text-sm text-[hsl(var(--muted-foreground))]">
                    {language === "es"
                        ? "Aún no tenemos suficientes transacciones para generar un plan personalizado."
                        : "Not enough transactions yet to generate a personalized plan."}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {plan.map((p, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                        >
                            <Card data-testid="action-plan-card" className="rounded-2xl bg-[hsl(var(--card))] border-[hsl(var(--border))] hover-lift h-full">
                                <CardHeader className="flex flex-row items-start justify-between">
                                    <CardTitle className="font-display text-lg">
                                        <span className="text-[hsl(var(--accent))] mr-2">0{i + 1}</span>{p.title}
                                    </CardTitle>
                                    {p.expected_savings > 0 && (
                                        <div className="text-right">
                                            <div className="text-xs uppercase text-[hsl(var(--muted-foreground))]">
                                                {language === "es" ? "Ahorro est." : "Est. savings"}
                                            </div>
                                            <div className="font-bold text-[hsl(var(--accent))]">{fmt(p.expected_savings)}</div>
                                        </div>
                                    )}
                                </CardHeader>
                                <CardContent>
                                    <ul className="space-y-2">
                                        {(p.steps || []).map((s, idx) => (
                                            <li key={idx} className="flex items-start gap-2 text-sm">
                                                <CheckCircle2 className="w-4 h-4 text-[hsl(var(--accent))] shrink-0 mt-0.5" />
                                                <span>{s}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </div>
            )}
        </AppLayout>
    );
};

export default ActionPlan;
